import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ContactTab } from "./profile/ContactTab";
import { AddressesTab } from "./profile/AddressesTab";
import { PaymentMethodsTab } from "./profile/PaymentMethodsTab";
import { CoiTab } from "./profile/CoiTab";

// Phase 4a customer-portal FR-9 — Profile shell with four tabs.
// Mobile: tabs stack as a horizontal scroll-strip. Desktop: two-
// column with the tab list on the left.
const TABS = [
  { key: "contact", label: "Contact" },
  { key: "addresses", label: "Address book" },
  { key: "payment", label: "Payment methods" },
  { key: "coi", label: "COI on file" }
] as const;

type TabKey = (typeof TABS)[number]["key"];

export function ProfilePage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = location.hash.replace("#", "") as TabKey;
  const [tab, setTab] = useState<TabKey>(
    TABS.some((t) => t.key === initialTab) ? initialTab : "contact"
  );

  function select(t: TabKey): void {
    setTab(t);
    navigate({ hash: `#${t}` }, { replace: true });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b border-zinc-200 pb-2">
        <h1 className="text-lg font-semibold">Profile</h1>
        <Link to="/portal" className="text-xs underline">
          ← Dashboard
        </Link>
      </div>

      <div className="md:flex md:gap-4">
        <nav className="-mx-1 flex gap-1 overflow-x-auto md:mx-0 md:w-48 md:flex-col">
          {TABS.map((t) => (
            <button
              type="button"
              key={t.key}
              onClick={() => select(t.key)}
              className={`min-h-[44px] whitespace-nowrap rounded px-3 py-2 text-sm ${
                tab === t.key ? "bg-zinc-900 text-white" : "text-zinc-700"
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="mt-2 flex-1 md:mt-0">
          {tab === "contact" ? <ContactTab /> : null}
          {tab === "addresses" ? <AddressesTab /> : null}
          {tab === "payment" ? <PaymentMethodsTab /> : null}
          {tab === "coi" ? <CoiTab /> : null}
        </div>
      </div>
    </div>
  );
}
